import { Box, Cluster } from '@a1s/ui';
import { rem } from 'polished';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation, Trans } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { setBypassSplashCookie } from './utils';

import { Text } from 'ui-new';
import Logo from 'ui/atoms/Logo';
import Opacity from 'ui/atoms/Opacity';
import Content from 'ui/molecules/Content';
import ContentBlock from 'ui/molecules/ContentBlock';
import LanguageSelector from 'ui/molecules/LanguageSelector';
import OneTrustButton from 'ui/molecules/OneTrustButton';

const ButtonWrapper = styled.div`
  color: ${({ theme }) => theme.colors.grayBlue};
  line-height: 1;
`;

const ContentContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  width: ${rem(670)};
`;

const Header = styled.header`
  margin-bottom: ${(p) => rem(p.theme.spacing.md)};
`;

const MessageBox = styled.div`
  background: ${({ theme }) => theme.colors.gray100};
  border-radius: ${rem(8)};
  padding: ${rem(32)} ${rem(40)};
`;

const StyledLink = styled.a`
  color: ${({ theme }) => theme.colors.blue} !important;
  cursor: pointer;
  text-decoration: none;

  &:visited {
    color: ${({ theme }) => theme.colors.blue} !important;
  }

  &:hover {
    text-decoration: underline;
  }
`;

export default function SplashPage() {
  const { t } = useTranslation('loginPage');
  const history = useHistory();
  const zeroTrustUrl = getZeroTrustDashboardUrl();

  const handleLoginClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setBypassSplashCookie();
    history.push('/users/login');
  };

  return (
    <Container>
      <Helmet title={`${t('pageTitle')} - ${t('const:names:area1security')}`} />

      <Header>
        <Logo link={false} />
      </Header>

      <MessageBox>
        <Text as="div" color="$gray600" css={{ 'p + p': { marginTop: '$3' } }} size="md">
          <Trans
            i18nKey="loginPage:splashMessage"
            components={[
              <p />,
              <StyledLink href={zeroTrustUrl} target="_blank" rel="noopener noreferrer" />,
              null,
              <StyledLink href="/users/login" onClick={handleLoginClick} />,
            ]}
          />
        </Text>
      </MessageBox>
    </Container>
  );
}

function Container({ children }: { children: React.ReactNode }) {
  return (
    <>
      <ContentBlock top={<ContentContainer>{children}</ContentContainer>} />
      <Content>
        <Opacity value={0.5}>
          &copy;
          {` ${new Date().getFullYear()} Cloudflare Area 1 Security`}
        </Opacity>
      </Content>
      <Box css={{ bottom: rem(16), position: 'fixed', right: rem(16) }}>
        <Cluster align="baseline" gap>
          <ButtonWrapper>
            <OneTrustButton />
          </ButtonWrapper>
          <LanguageSelector direction="up" />
        </Cluster>
      </Box>
    </>
  );
}

// Private functions
// -------------------------------------------------------------------------------------------------

function getApplicationEnv(): 'production' | 'staging' {
  const stage = process.env.REACT_APP_STAGE?.toLowerCase();
  const nodeEnv = process.env.NODE_ENV?.toLowerCase();

  return nodeEnv === 'production' || stage === 'production' ? 'production' : 'staging';
}

function getZeroTrustDashboardUrl(): string {
  const env = getApplicationEnv();

  return env === 'production'
    ? 'https://one.dash.cloudflare.com/?to=/:account/email-security/overview'
    : 'https://one.dash.staging.cloudflare.com/?to=/:account/email-security/overview';
}
